import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {KTSummaryCard} from 'helpers/ui-components/KTSummaryCard'
import {ToggleButton} from 'helpers/ui-components/ToggleButton'
import {useListController} from 'hooks/useListController'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import KTTable from 'helpers/ui-components/KTTable'
import KTPagination from 'helpers/ui-components/KTPagination'
import {ColumnProps, IFormField} from 'models/Common'
import Spinner from 'layout/common/Spinner'
import {Button, Model} from 'helpers'
import {
 fetchCropMasterList,
 updateCrop,
} from 'utils/api/cropMasterService'
import {ICropMaster} from 'models/CropMaster'
import {
 getAddCropPath,
 getCropDetailsPath,
} from './cropMasterRoutes'

const CROP_CATEGORY_LABELS: Record<string, string> = {
 FV: 'F&V',
 DRY: 'Dry',
}

const CROP_QUICK_FILTERS = [
 {key: '', value: 'All'},
 {key: 'Active', value: 'Active'},
 {key: 'Inactive', value: 'Inactive'},
 {key: 'DRY', value: CROP_CATEGORY_LABELS.DRY},
 {key: 'FV', value: CROP_CATEGORY_LABELS.FV},
]

type IPendingToggle = {
 record: ICropMaster
 nextActive: boolean
}

const CropMasterListing = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [selectedStatus, setSelectedStatus] = useState('')
 const [loader, setLoader] = useState(false)
 const [summary, setSummary] = useState({
  totalCount: 0,
  activeCount: 0,
 })
 const [pendingToggle, setPendingToggle] =
  useState<IPendingToggle | null>(null)

 const fetcher = useCallback(
  async (
   page: number,
   pageSize: number,
   query?: IFormField,
   signal?: AbortSignal
  ) => {
   const res = await fetchCropMasterList(
    page,
    pageSize,
    query,
    signal
   )
   setSummary({
    totalCount: res.total,
    activeCount: res.activeCount,
   })
   return {data: res.data, total: res.total}
  },
  []
 )

 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
  reload,
 } = useListController<ICropMaster>({
  fetcher,
  initialPageSize: 10,
  setLoader,
 })

 const handleToggleRequest = (
  record: ICropMaster,
  nextActive: boolean
 ) => {
  setPendingToggle({record, nextActive})
 }

 const handleCancelToggle = () => {
  setPendingToggle(null)
 }

 const handleConfirmToggle = async () => {
  if (!pendingToggle) return
  const {record, nextActive} = pendingToggle
  setPendingToggle(null)
  setLoader(true)
  const updated = await updateCrop(record.id, {
   is_active: nextActive,
  })
  setLoader(false)
  if (updated) {
   await reload()
  }
 }

 const columns: Array<ColumnProps<ICropMaster>> = [
  {
   key: 'id',
   title: t('cropMaster.cropTable.srNo'),
   className: 'w-[70px] text-center',
   render: (_column, _record, index?: number) => {
    if (index === undefined) return '-'

    return (currentPage - 1) * itemsPerPage + index + 1
   },
  },
  {
   key: 'crop_name',
   title: t('cropMaster.cropTable.cropName'),
  },
  {
   key: 'crop_category',
   title: t('cropMaster.cropTable.cropCategory'),
   render: (_, record) =>
    CROP_CATEGORY_LABELS[record.crop_category] ??
    record.crop_category,
  },
  {
   key: 'has_active_quality_parameter',
   title: t('cropMaster.cropTable.qualityParameterStatus'),
   className: 'text-center',
   render: (_, record) => (
    <span
     className={`inline-block text-center py-1 px-3 rounded-full text-xs font-medium ${
      record.has_active_quality_parameter
       ? 'bg-[#E3F6E8] text-[#0E7A41]'
       : 'bg-[#FDE9EB] text-[#CC293F]'
     }`}
    >
     {record.has_active_quality_parameter
      ? t('cropMaster.cropTable.added')
      : t('cropMaster.cropTable.notAdded')}
    </span>
   ),
  },
  {
   key: 'is_active',
   title: t('cropMaster.cropTable.active'),
   className: 'text-center',
   render: (_, record) => (
    <div className='flex items-center justify-center w-full'>
     <ToggleButton
      checked={record.is_active}
      onChange={(nextActive) =>
       handleToggleRequest(record, nextActive)
      }
     />
    </div>
   ),
  },
  {
   key: 'action',
   title: t('cropMaster.cropTable.action'),
   className: 'w-[120px] text-center',
   render: (_, record) => (
    <div className='flex items-center justify-center w-full'>
     <button
      type='button'
      className='text-center bg-transparent text-sm text-[#4E83E4] font-medium underline underline-offset-auto'
      onClick={() =>
       navigate(getCropDetailsPath(record.id))
      }
     >
      {t('cropMaster.cropTable.viewDetails')}
     </button>
    </div>
   ),
  },
 ]

 const applyCropFilters = (status: string) => {
  const filters: IFormField = {}
  if (status === 'Active') {
   filters.is_active = true
  } else if (status === 'Inactive') {
   filters.is_active = false
  } else if (status === 'DRY' || status === 'FV') {
   filters.crop_category = status
  }
  applyFilters(filters)
 }

 const handleStatusFilterClick = (value: string) => {
  setSelectedStatus(value)
  applyCropFilters(value)
 }

 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   {pendingToggle && (
    <Model
     show={Boolean(pendingToggle)}
     showHeader={false}
     closeBtnClass='absolute right-4 top-2 !bg-[#E6EBF0] !rounded-3xl w-7 h-7 !text-[#33393D]'
     header=''
     close={handleCancelToggle}
     modelClass='md:w-[500px]'
     modelContainerClass='!rounded-2xl'
     modelBodyClass='p-8'
    >
     <div className='w-full'>
      <p className='text-[#171B1F] text-left font-bold text-2xl mb-5'>
       {t('common.confirmModalTitle')}
      </p>
      <p className='text-[#33393D] text-base mb-7'>
       {pendingToggle.nextActive
        ? t('cropMaster.cropTable.activateConfirmMessage')
        : t(
           'cropMaster.cropTable.deactivateConfirmMessage'
          )}
      </p>
      <div className='grid md:grid-cols-2 gap-4'>
       <Button
        type='button'
        disabled={false}
        onClick={handleCancelToggle}
        color='red'
        className='text-center py-2.5 px-6 bg-white
         !rounded-lg text-base !text-[#CC293F] font-semibold border-[#CC293F] hover:!bg-[#FDE9EB]'
       >
        {t('common.cancel')}
       </Button>
       <Button
        type='button'
        disabled={false}
        onClick={() => {
         void handleConfirmToggle()
        }}
        color={pendingToggle.nextActive ? 'blue' : 'red'}
        className={`text-center py-2.5 px-6 !rounded-lg text-base !text-white font-semibold ${
         pendingToggle.nextActive
          ? '!bg-[#4E83E4] border-[#4E83E4] hover:!bg-white hover:!text-[#4E83E4]'
          : '!bg-[#CC293F] border-[#CC293F] hover:!bg-white hover:!text-[#CC293F]'
        }`}
       >
        {pendingToggle.nextActive
         ? t('cropMaster.cropTable.activate')
         : t('cropMaster.cropTable.deactivate')}
       </Button>
      </div>
     </div>
    </Model>
   )}
   <>
    <div className='mb-6 pb-6 border-b border-[#E6EBF0]'>
     <p className='text-[#2F2F2F] text-lg font-medium mb-4'>
      {t('cropMaster.cropTable.overallSummary')}
     </p>
     <div className='flex flex-wrap items-center gap-6'>
      <KTSummaryCard
       title={t('cropMaster.cropTable.noOfCrops')}
       count={summary.totalCount}
       containerClass='flex flex-col items-center justify-center w-[200px] !px-2 !py-2 bg-[#F5F5F5] border-[#E6EBF0]'
       color={''}
      />
      <KTSummaryCard
       title={t('cropMaster.cropTable.activeCrops')}
       count={summary.activeCount}
       containerClass='flex flex-col items-center justify-center w-[200px] !px-2 !py-2 bg-[#F5F5F5] border-[#E6EBF0]'
       color={''}
      />
     </div>
    </div>

    <p className='text-[#2F2F2F] text-lg font-medium mb-4'>
     {t('cropMaster.cropTable.allCrops')}
    </p>

    <div className='flex flex-wrap items-center justify-between gap-4'>
     <div className='flex flex-wrap items-center gap-4'>
      <div className='w-[320px]'>
       <KTSearchBox
        label={t('cropMaster.cropTable.search')}
        placeholder={t(
         'cropMaster.cropTable.searchPlaceholder'
        )}
        setSearchTerm={setSearchTerm}
       />
      </div>

      <div className='flex items-center flex-wrap gap-2'>
       <div className='text-[#33393D] text-sm mr-1'>
        {t('common.quickFilters')}
       </div>
       {CROP_QUICK_FILTERS.map((val, key) => (
        <div
         key={key}
         onClick={() => handleStatusFilterClick(val.key)}
         className={`${
          selectedStatus === val.key
           ? 'bg-[#4E83E4] text-white border-[#4E83E4]'
           : 'bg-white text-[#33393D] border-[#CED8E0]'
         } text-center py-1.5 px-4 rounded-full text-sm font-medium border hover:bg-[#4E83E4] hover:text-white cursor-pointer`}
        >
         {val.value}
        </div>
       ))}
      </div>
     </div>

     <Button
      type='button'
      disabled={false}
      onClick={() => navigate(getAddCropPath())}
      color='blue'
      className='text-center py-2.5 px-4 bg-[#4E83E4]
       !rounded-lg text-base text-white hover:bg-white hover:!text-[#4E83E4] font-semibold border-[#4E83E4] whitespace-nowrap'
     >
      + {t('cropMaster.cropTable.addNewCrop')}
     </Button>
    </div>

    <div className='mt-6'>
     <KTTable
      data={tableData}
      columns={columns}
      headerClass='bg-[#80868C]'
      rowClass='even:bg-[#F5F8FA]'
      colClass='!text-sm'
      showPagination={false}
     />
    </div>

    <div className='mt-4 flex flex-col'>
     <KTPagination
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={totalRecord}
      onPageChange={setCurrentPage}
      setItemsPerPage={(size) => {
       setItemsPerPage(size)
       setCurrentPage(1)
      }}
     />
    </div>
   </>
  </>
 )
}

export default CropMasterListing
